/** Nearest krona, same rounding as the per-task economy cells. */
function roundKr(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n);
}

function materialColumns(row) {
  if (row?.materialsFromInvoices != null || row?.materialsFromAccruals != null) {
    return {
      materials: roundKr(row.materialsFromInvoices),
      expected: roundKr(row.materialsFromAccruals),
    };
  }
  return {
    materials: roundKr(row?.materialsAndSubs),
    expected: 0,
  };
}

/**
 * Margin from the kronor already shown in the table.
 * Estimated cost − labour − materials − expected costs.
 * Each input is rounded to the nearest krona first, so the result matches
 * subtracting the cells rather than rounding only the final difference.
 * A blank estimated cost counts as 0, so cost with no price is a negative margin.
 */
export function perTaskMarginFromRounded({ price, labour, materials, expected }) {
  const hasActivity = price !== 0 || labour !== 0 || materials !== 0 || expected !== 0;
  if (!hasActivity) {
    return { margin: null, marginPct: null, price: 0, labour: 0, materials: 0, expected: 0 };
  }
  const margin = price - labour - materials - expected;
  return {
    margin,
    marginPct: price > 0 ? (margin / price) * 100 : null,
    price,
    labour,
    materials,
    expected,
  };
}

/** Margin for one task or subtask row, using that row's own figures (not subtask rollups). */
export function perTaskMarginForRow(row) {
  const cols = materialColumns(row);
  return perTaskMarginFromRounded({
    price: roundKr(row?.estimatedCost),
    labour: roundKr(row?.labourCost),
    materials: cols.materials,
    expected: cols.expected,
  });
}

/** This task's fixed price, and that price plus each subtask's fixed price. */
export function perTaskPriceParts(task) {
  const own = roundKr(task?.estimatedCost);
  const subPrice = (task?.subtasks || []).reduce((sum, sub) => sum + roundKr(sub?.estimatedCost), 0);
  return {
    own,
    total: own + subPrice,
    hasSubtasks: (task?.subtasks || []).length > 0,
  };
}

/**
 * Upper margin matches the upper cost cells and this task's estimated cost.
 * When the task has subtasks, total margin matches the parenthetical sums:
 * all estimated costs under the task, minus parenthetical labour, materials and expected costs.
 */
export function perTaskMargins(task) {
  const subs = task?.subtasks || [];
  const at = task?.atTaskLevel;
  const own = perTaskMarginForRow(
    at
      ? {
          estimatedCost: task.estimatedCost,
          labourCost: at.labourCost,
          materialsFromInvoices: at.materialsFromInvoices,
          materialsFromAccruals: at.materialsFromAccruals,
          materialsAndSubs: at.materialsAndSubs,
        }
      : task
  );
  if (subs.length === 0) return { own, total: own, hasSubtasks: false };

  const prices = perTaskPriceParts(task);
  const inc = task.includingSubtasks;
  let labour;
  let materials;
  let expected;
  if (inc) {
    const cols = materialColumns(inc);
    labour = roundKr(inc.labourCost);
    materials = cols.materials;
    expected = cols.expected;
  } else {
    const subSum = subs.reduce(
      (acc, sub) => {
        const margin = perTaskMarginForRow(sub);
        return {
          labour: acc.labour + margin.labour,
          materials: acc.materials + margin.materials,
          expected: acc.expected + margin.expected,
        };
      },
      { labour: 0, materials: 0, expected: 0 }
    );
    labour = own.labour + subSum.labour;
    materials = own.materials + subSum.materials;
    expected = own.expected + subSum.expected;
  }

  return {
    own,
    total: perTaskMarginFromRounded({ price: prices.total, labour, materials, expected }),
    hasSubtasks: true,
  };
}

/** Figures the total row adds once per task: the parenthetical sum when subtasks exist. */
export function displayedPerTaskTotal(task) {
  const margins = perTaskMargins(task);
  const subs = task?.subtasks || [];
  const inc = subs.length > 0 ? task?.includingSubtasks : null;
  const hours = Number(inc ? inc.reportedHours : task?.reportedHours);
  return {
    hours: Number.isFinite(hours) ? hours : 0,
    labour: margins.total.labour,
    materials: margins.total.materials,
    expected: margins.total.expected,
    price: margins.total.price,
    margin: margins.total.margin,
    marginPct: margins.total.marginPct,
  };
}

export function sumDisplayedPerTaskTotals(tasks) {
  return (tasks || []).reduce(
    (acc, task) => {
      const row = displayedPerTaskTotal(task);
      return {
        hours: acc.hours + row.hours,
        labour: acc.labour + row.labour,
        materials: acc.materials + row.materials,
        expected: acc.expected + row.expected,
        price: acc.price + row.price,
        margin: acc.margin + (row.margin ?? 0),
        hasMargin: acc.hasMargin || row.margin != null,
      };
    },
    { hours: 0, labour: 0, materials: 0, expected: 0, price: 0, margin: 0, hasMargin: false }
  );
}
